import axios from 'axios'
import { store as notificationStore } from 'react-notifications-component'
import store from '@redux/store'
import { logoutAction, setRefreshToken } from '@redux/actions/sessionActions'
import { getValidationResponse } from '@utils'
import { CUSTOM_ERROR_NOTIFICATION } from '@constants'
import {
  LOAD_ERROR_MESSAGE,
  LOAD_ERROR_NOTIFICATION_INTERVAL,
} from './clients.constants'

let lastLoadErrorNotificationAt = 0

// Shows an error notification, at most once per
// LOAD_ERROR_NOTIFICATION_INTERVAL across the whole app, so several failures
// at once don't stack toasts.
export const notifyLoadError = (message = LOAD_ERROR_MESSAGE) => {
  const now = Date.now()
  if (now - lastLoadErrorNotificationAt < LOAD_ERROR_NOTIFICATION_INTERVAL)
    return

  lastLoadErrorNotificationAt = now
  notificationStore.addNotification(CUSTOM_ERROR_NOTIFICATION(message))
}

// The interceptor isn't a component, so it reads the flag from window._env_
// directly rather than through useFeatureFlag.
const isMaintenancePageEnabled = () =>
  window._env_?.REACT_APP_FEATURE_FLAG_MAINTENANCE_PAGE === 'true'

// Reports a failed load: with the maintenance_page flag on, the agent is sent
// to the maintenance page; otherwise an error notification is shown.
export const handleLoadError = (message) => {
  if (isMaintenancePageEnabled()) {
    window.location.replace('/maintenance')
    return
  }

  notifyLoadError(message)
}

export const cleanup = () => {
  sessionStorage.removeItem('refreshToken')
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('user')
  store.dispatch(logoutAction())
  window.location.replace('/login')
}

export const AxiosRefreshClientHandlers = {
  handleRequest: (config) => {
    const refreshToken = sessionStorage.getItem('refreshToken')
    config.headers.Authorization = refreshToken ? `Bearer ${refreshToken}` : ''

    return config
  },
  handleResponse: async (err) => {
    if (err?.response?.status === 403 || err?.response?.status === 401)
      cleanup()
  },
}

export const AxiosAccessClientHandlers = {
  handleRequest: (config) => {
    const token = sessionStorage.getItem('token')
    config.headers.Authorization = token ? `Bearer ${token}` : ''

    return config
  },
  handleResponse: async (err, axiosClient, axiosClientRefresh) => {
    const token = sessionStorage.getItem('token')
    const originalConfig = err.config
    // Read from the request config, which axios keeps on the error even when
    // there is no response (timeouts, network errors), so a failed write is
    // never reported as a failed load.
    const method = originalConfig?.method

    if (err?.response?.status === 409) return Promise.reject(err)

    if (err?.response?.status !== 403 && err?.response?.status !== 401) {
      // A failed GET is reported through handleLoadError. Opt-out for
      // background/polled requests (e.g. the ClearStake status poll): a
      // transient failure there must not be reported at all. The caller sets
      // `skipErrorNotification` on the request. The app doesn't cancel
      // requests today; the isCancel check is defensive, so a future
      // cancellation doesn't show an error.
      if (
        getValidationResponse(method) &&
        !originalConfig?.skipErrorNotification &&
        !axios.isCancel(err)
      )
        handleLoadError()
      return Promise.reject(err)
    }

    if (err?.response?.status === 403) {
      cleanup()
      return Promise.reject(err)
    }

    if (err?.response?.status === 401) {
      if (!token) {
        cleanup()
        return Promise.reject(err)
      }

      try {
        const res = await axiosClientRefresh.post('/admins/refresh')
        sessionStorage.setItem('token', res.data.result.token)
        sessionStorage.setItem('time', new Date())
        axios.defaults.headers.common.Authorization = `Bearer ${res.data.token}`
        store.dispatch(setRefreshToken(res.data.result))
        return axiosClient.request(originalConfig)
      } catch (e) {
        store.dispatch(setRefreshToken(false))
        return Promise.reject(err)
      }
    }

    return Promise.reject(err)
  },
}
